import * as React from 'react';
import Button from '@mui/material/Button';
import { Modal, Box, Fade, Backdrop } from '@mui/material';
import { Form } from 'react-final-form';
import * as Yup from 'yup';
import { makeValidate, TextField } from 'mui-rff';
import styles from './Authenticate.module.scss';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';

import logo from '../assets/logo.png'; // change this path to your actual logo path

const schema = Yup.object().shape({
  dob: Yup.string()
    .required('Date of birth is required')
    .matches(
      /^(0[1-9]|[12][0-9]|3[01])[-./](0[1-9]|1[012])[-./]\d{4}$|^\d{8}$/,
      'Please use DD-MM-YYYY, DD.MM.YYYY, DD/MM/YYYY, or DDMMYYYY'
    ),
});

const validate = makeValidate(schema);

type AuthenticateProps = {
  openAuthenticate: boolean;
  setOpenAuthenticate: React.Dispatch<React.SetStateAction<boolean>>;
  consentId: string;
};

function Authenticate({
  openAuthenticate,
  setOpenAuthenticate,
  consentId,
}: AuthenticateProps) {
  const [authError, setAuthError] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isDownloadPage = location.pathname.includes('/download');

  useQuery(
    ['checkIfLatestVersion', consentId],
    () =>
      axios
        .get(`/patient/consent_bundle/${consentId}/check_if_latest_version`)
        .then((res) => res.data),
    {
      enabled: !!consentId,
      onSuccess: (data) => {
        if (data !== true) {
          navigate(`/${data}`);
        }
      },
    }
  );

  const authenticate = useMutation(
    (dob: string) =>
      axios.get(`/patient/authenticate?consent_bundle_id=${consentId}&dob=${dob}`),
    {
      onSuccess: (response) => {
        if (response.data) {
          setAuthError(false);
          setOpenAuthenticate(false);
        } else {
          setAuthError(true);
        }
      },
      onError: () => {
        setAuthError(true);
      },
    }
  );

  const downloadPdf = (dob: string) => {
    setAuthError(false);

    axios
      .get(`/patient/authenticate?consent_bundle_id=${consentId}&dob=${dob}`)
      .then((response) => {
        if (response.data) {
          navigate(`/${consentId}/download/pdf`);
        } else {
          setAuthError(true);
        }
      })
      .catch(() => {
        setAuthError(true);
      });
  };

  const sendHelpData = (data: { dob: string }) => {
    setAuthError(false);
    authenticate.mutate(data.dob);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedCharacters = /[0-9\-\/.]/;
    const key = e.key;

    if (
      key === 'Backspace' ||
      key === 'Delete' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'Tab'
    ) {
      return;
    }

    if (!allowedCharacters.test(key)) {
      e.preventDefault();
    }
  };

  return (
    <Modal
      open={openAuthenticate}
      onClose={() => setOpenAuthenticate(false)}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 250,
        },
      }}
    >
      <Fade in={openAuthenticate}>
        <Box className={styles.modalWrapper}>
          <div className={styles.card}>
            <div className={styles.branding}>
              <img src={logo} alt="Company Logo" className={styles.logo} />
            </div>

            <div className={styles.header}>
              <div className={styles.iconCircle}>
                <span className={styles.lockIcon}>🔒</span>
              </div>
              <h2>Verify your identity</h2>
              <p>
                Enter your date of birth to securely access the information
                prepared by your dentist.
              </p>
            </div>

            <Form
              onSubmit={sendHelpData}
              validate={validate}
              render={({ handleSubmit, values, submitting, invalid }) => (
                <form
                  className={styles.headerForm}
                  onSubmit={handleSubmit}
                  noValidate
                >
                  <div className={styles.helperText}>
                    Please enter your birthday in one of these formats:
                    <strong> DDMMYYYY</strong>, <strong>DD-MM-YYYY</strong>,{' '}
                    <strong>DD/MM/YYYY</strong>, or <strong>DD.MM.YYYY</strong>
                  </div>

                  <TextField
                    label="Date of Birth"
                    name="dob"
                    required
                    fullWidth
                    variant="outlined"
                    onKeyDown={handleKeyDown}
                    placeholder="DDMMYYYY"
                  />

                  {authError && (
                    <div className={styles.errorBanner}>
                      Invalid date of birth. Please try again.
                    </div>
                  )}

                  <div className={styles.actions}>
                    <Button
                      type="button"
                      variant="outlined"
                      className={styles.secondaryButton}
                      onClick={() => setOpenAuthenticate(false)}
                    >
                      Cancel
                    </Button>

                    {!isDownloadPage ? (
                      <Button
                        className={styles.submitButton}
                        type="submit"
                        variant="contained"
                        disabled={submitting || authenticate.isLoading || invalid}
                      >
                        {authenticate.isLoading ? 'Authenticating...' : 'Authenticate'}
                      </Button>
                    ) : (
                      <Button
                        className={styles.submitButton}
                        type="button"
                        variant="contained"
                        disabled={authenticate.isLoading || !values?.dob}
                        onClick={() => downloadPdf(values.dob)}
                      >
                        {authenticate.isLoading
                          ? 'Checking...'
                          : 'Authenticate to Download'}
                      </Button>
                    )}
                  </div>
                </form>
              )}
            />

            <div className={styles.footer}>
              <a href="/privacy-policy" target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
            </div>
          </div>
        </Box>
      </Fade>
    </Modal>
  );
}

export default Authenticate;